import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import { clampPan, fitView, panBy, wheelFactor, zoomAt, zoomLimits, type Size, type ViewTransform } from '../../../lib/preview-math.js';

const PAD = 4;

export interface ZoomPanOptions {
  /** Natural size of the content; null until known. */
  content: Size | null;
  /** Plain drags call this (orbit) instead of panning; Shift-drag, right and middle drags still pan. */
  onDrag?: (dx: number, dy: number) => void;
  /** Refit also resets whatever `onDrag` controls. */
  onReset?: () => void;
}

/**
 * Wheel zoom toward the cursor, drag to pan, double click / Fit to refit, and
 * automatic refit on load and on resize while the user has not zoomed.
 */
export function useZoomPan({ content, onDrag, onReset }: ZoomPanOptions) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<Size>({ width: 0, height: 0 });
  const [view, setView] = useState<ViewTransform>({ x: 0, y: 0, scale: 1 });
  const fitted = useRef(true);
  const drag = useRef<{ x: number; y: number; pan: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);
  const latest = useRef({ view, box, content });
  latest.current = { view, box, content };

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setBox((b) => (b.width === el.clientWidth && b.height === el.clientHeight ? b : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Refit on load / resize / content change while the user has not moved the view.
  useEffect(() => {
    if (content && fitted.current && box.width > 0) setView(fitView(content, box, PAD));
  }, [content?.width, content?.height, box.width, box.height]); // eslint-disable-line react-hooks/exhaustive-deps

  const fit = useCallback(() => {
    const { content: c, box: b } = latest.current;
    fitted.current = true;
    if (c && b.width > 0) setView(fitView(c, b, PAD));
    onReset?.();
  }, [onReset]);

  const zoomBy = useCallback((factor: number, cx?: number, cy?: number) => {
    const { view: v, box: b, content: c } = latest.current;
    if (!c) return;
    const limits = zoomLimits(fitView(c, b, PAD).scale);
    fitted.current = false;
    setView(clampPan(zoomAt(v, cx ?? b.width / 2, cy ?? b.height / 2, factor, limits.min, limits.max), c, b));
  }, []);

  // Wheel must be a non-passive native listener to stop the page scrolling.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomBy(wheelFactor(e.deltaY, e.deltaMode), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, pan: !onDrag || e.shiftKey || e.button !== 0 };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    drag.current = { ...d, x: e.clientX, y: e.clientY };
    if (d.pan) {
      const { box: b, content: c } = latest.current;
      fitted.current = false;
      setView((v) => (c ? clampPan(panBy(v, dx, dy), c, b) : v));
    } else onDrag?.(dx, dy);
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    drag.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = 40;
    const pan = (dx: number, dy: number) => {
      const { box: b, content: c } = latest.current;
      if (!c) return;
      fitted.current = false;
      setView((v) => clampPan(panBy(v, dx, dy), c, b));
    };
    switch (e.key) {
      case '+':
      case '=':
        zoomBy(1.25);
        break;
      case '-':
      case '_':
        zoomBy(0.8);
        break;
      case '0':
        fit();
        break;
      case 'ArrowLeft':
        if (onDrag) onDrag(-12, 0);
        else pan(step, 0);
        break;
      case 'ArrowRight':
        if (onDrag) onDrag(12, 0);
        else pan(-step, 0);
        break;
      case 'ArrowUp':
        if (onDrag) onDrag(0, -12);
        else pan(0, step);
        break;
      case 'ArrowDown':
        if (onDrag) onDrag(0, 12);
        else pan(0, -step);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return {
    boxRef,
    view,
    dragging,
    fit,
    zoomBy,
    boxProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick: fit,
      onKeyDown,
      onContextMenu: (e: { preventDefault(): void }) => e.preventDefault(),
      tabIndex: 0,
    },
  };
}
